import { TruncatedTooltip } from '@common/components/TruncatedTooltip';
import { ChevronDownIcon, ResetIcon } from '@radix-ui/react-icons';
import {
    Badge,
    Box,
    Button,
    DropdownMenu,
    Flex,
    IconButton,
    Text,
} from '@radix-ui/themes';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { UseMetadataFieldReturn } from '../hooks/useMetadataField';

export interface MetadataFieldWrapperProps<TValue>
    extends UseMetadataFieldReturn<TValue> {
    children: ReactNode;
}

export const MetadataFieldWrapper = <TValue,>({
    field,
    isDirty,
    activeSource,
    availableSources,
    defaultSource,
    handleSourceChange,
    handleRevert,
    children,
}: MetadataFieldWrapperProps<TValue>) => {
    const { t } = useTranslation('metadataEditor');

    return (
        <Box
            p='3'
            minWidth='0'
            style={{
                backgroundColor: isDirty ? 'var(--accent-a2)' : 'transparent',
                border: '1px solid',
                borderColor: isDirty ? 'var(--accent-a6)' : 'var(--gray-a4)',
                borderRadius: 'var(--radius-4)',
                transition:
                    'background-color 0.2s ease, border-color 0.2s ease',
            }}
        >
            <Flex justify='between' align='center' mb='2' gap='1'>
                <Box pr={'3'} flexGrow={'1'} minWidth={'0'}>
                    <Text as='div' size='2' weight='bold' color='gray'>
                        <TruncatedTooltip>
                            {t(`fields.${field.name}`)}
                        </TruncatedTooltip>
                    </Text>
                </Box>

                <Flex align='center' gap='3' flexShrink={'0'}>
                    {isDirty && (
                        <IconButton
                            type='button'
                            size='1'
                            radius='full'
                            variant='ghost'
                            color='orange'
                            onClick={handleRevert}
                        >
                            <ResetIcon width='14' height='14' />
                        </IconButton>
                    )}

                    <DropdownMenu.Root modal={false}>
                        <DropdownMenu.Trigger>
                            <Button
                                type='button'
                                size='1'
                                variant='ghost'
                                color={
                                    activeSource === 'user_overrides'
                                        ? undefined
                                        : 'gray'
                                }
                            >
                                <Text size='1' weight='medium'>
                                    {t(`sources.${activeSource}`)}
                                </Text>
                                <ChevronDownIcon width='12' height='12' />
                            </Button>
                        </DropdownMenu.Trigger>

                        <DropdownMenu.Content
                            size='1'
                            align='end'
                            variant='soft'
                        >
                            {availableSources.map((source) => {
                                const isSelected = activeSource === source.key;
                                const isDefault = defaultSource === source.key;
                                return (
                                    <DropdownMenu.Item
                                        key={source.key}
                                        onSelect={() =>
                                            handleSourceChange(source.key)
                                        }
                                        style={{
                                            backgroundColor: isSelected
                                                ? 'var(--accent-a3)'
                                                : 'transparent',
                                        }}
                                    >
                                        <Text
                                            size='1'
                                            weight={
                                                isSelected ? 'bold' : 'medium'
                                            }
                                            style={{
                                                color: isSelected
                                                    ? 'var(--accent-11)'
                                                    : 'inherit',
                                            }}
                                        >
                                            {t(`sources.${source.key}`)}
                                        </Text>

                                        <Box maxWidth='150px'>
                                            {source.displayValue ? (
                                                <TruncatedTooltip>
                                                    {source.displayValue}
                                                </TruncatedTooltip>
                                            ) : (
                                                '-'
                                            )}
                                        </Box>

                                        {isDefault &&
                                            source.key !== 'user_overrides' && (
                                                <Badge
                                                    size='1'
                                                    variant='surface'
                                                    color='gray'
                                                    style={{
                                                        fontSize: '9px',
                                                        padding: '0 4px',
                                                        minHeight: '16px',
                                                    }}
                                                >
                                                    {t('default')}
                                                </Badge>
                                            )}
                                    </DropdownMenu.Item>
                                );
                            })}
                        </DropdownMenu.Content>
                    </DropdownMenu.Root>
                </Flex>
            </Flex>

            <Box minWidth='0'>{children}</Box>
        </Box>
    );
};
